import { ApiError, apiErrorCode } from "@/lib/api";
import type {
  PreapprovalApplication,
  PreapprovalApplicationStatus,
} from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";

// BRK-S324 — the insured's current pre-approval application, on the account's
// Surety Program panel (consuming PMS-S719, ahead of the pin).
//
// Daniel's accounts-page feedback: once an application exists the panel shows
// ITS state instead of a bare "Get pre-approved". While one is open (draft,
// checks running, under review) there is no second application; a draft
// offers Continue (and the per-step pencils, BRK-S197); under review is the
// underwriter's and offers nothing; a refusal offers Re-apply, which starts a
// NEW application prefilled from it (a submitted one is never sent back to
// draft). PMS decides `canEdit` and `canReapply`; this file renders them.
//
// DISCRETION: a status and a date, never a score, a tier or a reason.

const STATUSES: readonly PreapprovalApplicationStatus[] = [
  "draft",
  "running",
  "under_review",
  "approved",
  "not_granted",
];

/** The published block, or null — absent, null, malformed or carrying a
 *  status this build has not mirrored all read as "nothing to show", which
 *  leaves the panel as it was. */
export function readPreapprovalApplication(
  value: unknown,
): (PreapprovalApplication & { status: PreapprovalApplicationStatus }) | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const status = STATUSES.find((s) => s === v.status);
  if (status === undefined) return null;
  if (typeof v.orderId !== "string" || v.orderId === "") return null;
  return {
    orderId: v.orderId,
    status,
    updatedAt: typeof v.updatedAt === "string" ? v.updatedAt : "",
    canEdit: v.canEdit === true,
    canReapply: v.canReapply === true,
  };
}

/** Is an application open — so no second one may start? */
export function isApplicationOpen(
  status: PreapprovalApplicationStatus,
): boolean {
  return (
    status === "draft" || status === "running" || status === "under_review"
  );
}

const LABELS: Record<PreapprovalApplicationStatus, string> = {
  draft: "Draft",
  running: "Checks running",
  under_review: "Under review by your underwriter",
  approved: "Approved",
  // Not "declined": a refusal is not a decline (PMS-E015).
  not_granted: "Not granted",
};

export function applicationStatusLabel(
  status: PreapprovalApplicationStatus,
): string {
  return LABELS[status];
}

export const APPLICATION_OPEN_COPY =
  "An application is already open for this insured.";
export const REAPPLY_NOT_ALLOWED_COPY =
  "Re-apply isn’t available for this application any more. Reload the page to see where it stands.";

/** A failed Re-apply, in the portal's words, plus the open application's
 *  order when PMS names it (`details.orderId` on APPLICATION_OPEN). Both codes
 *  are read as plain strings until the pin publishes them (PMS-S719). */
export function reapplyRefusal(err: unknown): {
  message: string;
  openOrderId: string | null;
} {
  const code = apiErrorCode(err);
  if (code === "APPLICATION_OPEN") {
    return { message: APPLICATION_OPEN_COPY, openOrderId: detailsOrderId(err) };
  }
  if (code === "REAPPLY_NOT_ALLOWED") {
    return { message: REAPPLY_NOT_ALLOWED_COPY, openOrderId: null };
  }
  return { message: messageFor(err), openOrderId: null };
}

function detailsOrderId(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const id = (details as { orderId?: unknown }).orderId;
  return typeof id === "string" && id !== "" ? id : null;
}
